import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { hotKeywords, movies } from "@/mocks/movies";

const sortOptions = ["综合", "最新更新", "最热", "评分"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [keyword, setKeyword] = useState(query);
  const [sort, setSort] = useState("综合");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta(
    query ? `搜索“${query}” - 91在线视频` : "影视搜索 - 91在线视频",
    query
      ? `91在线视频为您找到与“${query}”相关的影视作品，涵盖电影、电视剧及综艺，支持多种排序方式，快速定位想看的内容。`
      : "在91在线视频搜索电影、电视剧及综艺节目，快速定位你想看的高清影视资源。",
  );

  const results = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    const matched = movies.filter((m) => {
      const haystack = [
        m.title,
        m.director,
        m.region,
        ...m.genres,
        ...m.actors,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    if (sort === "最新更新") matched.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    else if (sort === "最热") matched.sort((a, b) => b.views - a.views);
    else if (sort === "评分") matched.sort((a, b) => b.rating - a.rating);
    return matched;
  }, [query, sort]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  const runKeyword = (word: string) => {
    setSearchParams({ q: word });
  };

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <h1 className="mt-4 font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
        影视搜索
      </h1>

      <form
        onSubmit={submit}
        className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
      >
        <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2.5">
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入影视名称、演员、导演或类型关键词"
            className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
          />
          {keyword && (
            <button
              type="button"
              aria-label="清空"
              onClick={() => setKeyword("")}
              className="flex h-5 w-5 items-center justify-center text-foreground-400 transition-colors hover:text-accent-500"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-600 text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      {!query ? (
        <div className="mt-10">
          <div className="mb-4 flex items-center gap-2">
            <i className="ri-fire-line text-accent-500" />
            <h2 className="font-heading text-lg font-600 text-foreground-950">最近热门搜索</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => runKeyword(word)}
                className="whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-4 py-2 text-sm text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              搜索“<span className="font-600 text-foreground-950">{query}</span>”，共找到{" "}
              <span className="font-600 text-accent-500">{results.length}</span> 个结果
            </p>
            <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setSort(option)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                    sort === option
                      ? "bg-primary-500 font-600 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {results.length === 0 ? (
            <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300/60 py-20 text-center text-foreground-500">
              <i className="ri-search-eye-line text-5xl" />
              <p className="text-base font-600 text-foreground-700">没有找到相关影视作品</p>
              <p className="text-sm">换个关键词试试，或看看下面的热门推荐</p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {hotKeywords.slice(0, 4).map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => runKeyword(word)}
                    className="whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-4 py-2 text-sm text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
                  >
                    {word}
                  </button>
                ))}
              </div>
              <Link
                to="/category/movie"
                className="mt-2 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-600 text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-compass-3-line" />
                浏览影视分类
              </Link>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}